import { PublicAccessCarReportSummaryDto, PublicReportDto } from '../types/models.types';
import { SVGIcon } from '@/components/SVGIcon/SVGIcon';
import { CardSummaryBoxProps } from '@/components/CardSummaryBox/CardSummaryBox';
import { getRatingMessage } from '@/helpers/ratingState';
import { mapCarBodySectionToUI } from './car-body-section.mapper';
import { mapCarInteriorSectionToUI } from './car-interior-section.mapper';
import { mapEngineAndTrunkSectionToUI } from './engine-trunk-section.mapper';
import { mapChassisSectionToUI } from './chassis-section.mapper';
import { mapTestDriveSectionToUI } from './test-drive-section.mapper';
import { mapComputerDiagnosticsSectionToUI } from './computer-diagnostics-section.mapper';

export interface MappedReportSummary {
  summaryCards: CardSummaryBoxProps[];
  overallRating: number | null;
  expertWouldBuy: boolean | null;
  inspectionSummary: string;
  mileageVerificationDescription: string;
  mileageVerificationIsPositive: boolean | null;
  advertisementVerificationDescription: string;
  advertisementVerificationIsPositive: boolean | null;
  sellerInterviewDescription: string;
}

const mapYesNoToBoolean = (value?: 'YES' | 'NO' | null): boolean | null => {
  if (!value) return null;
  return value === 'YES';
};

const mapScoreToNumber = (score?: string | null): number | null => {
  if (!score) return null;

  const numericScore = parseFloat(score);
  if (isNaN(numericScore)) return null;

  return Math.max(1, Math.min(5, numericScore));
};

const mapSummaryCards = (raport: PublicReportDto): CardSummaryBoxProps[] => {
  const carBody = mapCarBodySectionToUI(raport.carBodySection);
  const carInterior = mapCarInteriorSectionToUI(raport.carInteriorSection);
  const engineAndTrunk = mapEngineAndTrunkSectionToUI(raport.engineAndTrunkCompartmentSection);
  const testDrive = mapTestDriveSectionToUI(raport.carTestDriveSection);
  const chassis = mapChassisSectionToUI(raport.carChassisSection);
  const computerDiagnostics = mapComputerDiagnosticsSectionToUI(raport.computerDiagnosticsSection);


  return [
    {
      icon: <SVGIcon name="car" />,
      title: 'Nadwozie',
      description: 'Grubość lakieru / Szyby / Ogumienie / Światła',
      rating: carBody?.ratingSection?.rating || 0,
      maxRating: 5,
      ratingText: getRatingMessage(carBody?.ratingSection?.rating || 0),
      targetId: 'car-body',
    },
    {
      icon: <SVGIcon name="trophy" />,
      title: 'Wnętrze',
      description: 'Kokpit / Elementy wnętrza / Wyposażenie',
      rating: carInterior?.ratingSection?.rating || 0,
      maxRating: 5,
      ratingText: getRatingMessage(carInterior?.ratingSection?.rating || 0),
      targetId: 'car-interior',
    },
    {
      icon: <SVGIcon name="handshake" />,
      title: 'Silnik i bagażnik',
      description: 'Komora silnika / Komora bagażnika',
      rating: engineAndTrunk?.ratingSection?.rating || 0,
      maxRating: 5,
      ratingText: getRatingMessage(engineAndTrunk?.ratingSection?.rating || 0),
      targetId: 'engine-trunk',
    },
    {
      icon: <SVGIcon name="steering" />,
      title: 'Jazda próbna',
      description: 'Podsumowanie 20 minutowej jazdy próbnej',
      rating: testDrive?.ratingSection?.rating || 0,
      maxRating: 5,
      ratingText: getRatingMessage(testDrive?.ratingSection?.rating || 0),
      targetId: 'test-drive',
    },
    {
      icon: <SVGIcon name="network-nodes" />,
      title: 'Podwozie',
      description: 'Zawieszenie / Układ hamulcowy / Układ napędowy',
      rating: chassis?.ratingSection?.rating || 0,
      maxRating: 5,
      ratingText: getRatingMessage(chassis?.ratingSection?.rating || 0),
      targetId: 'chassis',
    },
    {
      icon: <SVGIcon name="monitor-screen" />,
      title: 'Diagnostyka komputerowa',
      description: 'Błędy i nieprawidłowości wykryte podczas badania komputerem diagnostycznym',
      isOk: computerDiagnostics?.isCommunicationSuccessful === true && computerDiagnostics?.hasErrors === false,
      requiresAttention: computerDiagnostics ? (!computerDiagnostics.isCommunicationSuccessful || computerDiagnostics.hasErrors) : false,
      ratingText: computerDiagnostics?.hasErrors ? 'Wymaga uwagi' : 'Brak zastrzeżeń',
      targetId: 'computer-diagnostics',
    },
  ];
};

export const mapReportSummaryToUI = (
  raport: PublicReportDto | null | undefined,
): MappedReportSummary | null => {
  const summary: PublicAccessCarReportSummaryDto | undefined = raport?.carReportSummarySection;

  if (!raport) { return null; }

  if (!summary) {
    return {
      summaryCards: [],
      overallRating: null,
      expertWouldBuy: null,
      inspectionSummary: '',
      mileageVerificationDescription: '',
      mileageVerificationIsPositive: null,
      advertisementVerificationDescription: '',
      advertisementVerificationIsPositive: null,
      sellerInterviewDescription: '',
    };
  }

  const conclusions = summary.conclusions;
  const finalEvaluation = summary.finalEvaluation;

  return {
    summaryCards: mapSummaryCards(raport),
    overallRating: mapScoreToNumber(finalEvaluation?.finalScore),
    expertWouldBuy: mapYesNoToBoolean(
      finalEvaluation?.appraiserPurchaseDecision,
    ),
    inspectionSummary:
      finalEvaluation?.appraiserPurchaseDecisionRationale || 'Brak podsumowania rzeczoznawcy',
    mileageVerificationDescription:
      conclusions?.mileageVerificationRationale || 'Brak podsumowania rzeczoznawcy',
    mileageVerificationIsPositive: mapYesNoToBoolean(
      conclusions?.mileageVerification,
    ),
    advertisementVerificationDescription:
      conclusions?.advertisementVerificationRationale || 'Brak podsumowania rzeczoznawcy',
    advertisementVerificationIsPositive: mapYesNoToBoolean(
      conclusions?.advertisementVerification,
    ),
    sellerInterviewDescription:
      conclusions?.sellerInterviewConclusions || 'Brak podsumowania rzeczoznawcy',
  };
};
